import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import {
  GeneratedStatement,
  StatementSection,
  StatementFilters,
  SurveyMetadata
} from '../../models/statement-generator.models';
import * as XLSX from 'xlsx';

/**
 * Statement Review Component
 * Displays generated statements with full details and selection capabilities
 */
@Component({
  selector: 'app-statement-review',
  templateUrl: './statement-review.component.html',
  styleUrls: ['./statement-review.component.scss'],
  standalone: false
})
export class StatementReviewComponent implements OnInit {
  @Input() statements: GeneratedStatement[] = [];
  @Input() sections: StatementSection[] = [];
  @Input() surveyMetadata: SurveyMetadata | null = null;
  @Input() selectedStatementIds: number[] = [];

  @Output() selectionChange = new EventEmitter<number[]>();
  @Output() addToSurvey = new EventEmitter<number[]>();
  @Output() exportStatements = new EventEmitter<GeneratedStatement[]>();

  // Filtering and sorting
  filters: StatementFilters = {};
  searchText = '';
  selectedSection = 'all';
  selectedEvidenceLevel = 'all';
  selectedComplianceRisk = 'all';
  showOnlySelected = false;

  // View mode
  viewMode: 'cards' | 'table' = 'cards';

  // Expanded statement for details
  expandedStatementId: number | null = null;

  ngOnInit(): void {
    console.log('Statement Review initialized with', this.statements.length, 'statements');
  }

  /**
   * Get filtered and sorted statements
   */
  get filteredStatements(): GeneratedStatement[] {
    let filtered = [...this.statements];

    // Apply filters
    if (this.selectedSection !== 'all') {
      filtered = filtered.filter(s => s.section === this.selectedSection);
    }

    if (this.selectedEvidenceLevel !== 'all') {
      filtered = filtered.filter(s => s.level_of_evidence === this.selectedEvidenceLevel);
    }

    if (this.selectedComplianceRisk !== 'all') {
      filtered = filtered.filter(s => {
        const risk = s.compliance?.off_label_risk || (s.compliance as any)?.off_label_tone_risk_flag;
        return risk === this.selectedComplianceRisk;
      });
    }

    if (this.searchText) {
      const search = this.searchText.toLowerCase();
      filtered = filtered.filter(s =>
        s.statement_text?.toLowerCase().includes(search) ||
        s.rationale?.toLowerCase().includes(search) ||
        s.why_it_matters?.toLowerCase().includes(search)
      );
    }

    if (this.showOnlySelected) {
      filtered = filtered.filter(s => this.selectedStatementIds.includes(s.id));
    }

    return filtered;
  }

  /**
   * Get unique sections for filter
   */
  get uniqueSections(): string[] {
    const sections = new Set(this.statements.map(s => s.section));
    return Array.from(sections);
  }

  /**
   * Get unique evidence levels for filter
   */
  get uniqueEvidenceLevels(): string[] {
    const levels = new Set(this.statements.map(s => s.level_of_evidence));
    return Array.from(levels);
  }

  /**
   * Check if statement is selected
   */
  isSelected(statementId: number): boolean {
    return this.selectedStatementIds.includes(statementId);
  }

  /**
   * Toggle statement selection
   */
  toggleSelection(statementId: number): void {
    const statement = this.statements.find(s => s.id === statementId);
    if (statement?.is_in_draft_survey) return;

    const index = this.selectedStatementIds.indexOf(statementId);
    let newSelection: number[];

    if (index > -1) {
      newSelection = this.selectedStatementIds.filter(id => id !== statementId);
    } else {
      newSelection = [...this.selectedStatementIds, statementId];
    }

    this.selectionChange.emit(newSelection);
  }

  /**
   * Select all filtered statements
   */
  selectAll(): void {
    const allIds = this.filteredStatements.filter(s => !s.is_in_draft_survey).map(s => s.id);
    const newSelection = [...new Set([...this.selectedStatementIds, ...allIds])];
    this.selectionChange.emit(newSelection);
  }

  /**
   * Deselect all
   */
  deselectAll(): void {
    this.selectionChange.emit([]);
  }

  /**
   * Toggle expanded view for a statement
   */
  toggleExpanded(statementId: number): void {
    this.expandedStatementId = this.expandedStatementId === statementId ? null : statementId;
  }

  /**
   * Check if statement is expanded
   */
  isExpanded(statementId: number): boolean {
    return this.expandedStatementId === statementId;
  }

  /**
   * Handle add to survey
   */
  handleAddToSurvey(): void {
    if (this.selectedStatementIds.length === 0) {
      return;
    }
    this.addToSurvey.emit(this.selectedStatementIds);
  }

  /**
   * Handle export (legacy emit)
   */
  handleExport(): void {
    const selectedStatements = this.statements.filter(s =>
      this.selectedStatementIds.includes(s.id)
    );
    this.exportStatements.emit(selectedStatements);
  }

  /**
   * Export selected statements to Excel (.xlsx)
   */
  exportSelectedToExcel(): void {
    const selected = this.statements.filter(s => this.selectedStatementIds.includes(s.id));
    if (selected.length === 0) return;

    const rows = selected.map(s => ({
      ID: s.id,
      Statement: s.statement_text || '',
      Section: s.section || '',
      Rationale: s.rationale || '',
      'Why It Matters': s.why_it_matters || '',
      'Level of Evidence': s.level_of_evidence || '',
      'Grade of Recommendation': s.grade_of_recommendation || '',
      'Compliance Risk': s.compliance?.off_label_risk || (s.compliance as any)?.off_label_tone_risk_flag || '',
      'Confidence Score': s.confidence_score ?? '',
      Comment: s.comment || '',
      References: Array.isArray(s.reference_links)
        ? s.reference_links.map(r => (typeof r === 'string' ? r : (r as any)?.url || (r as any)?.title || '')).join('; ')
        : ''
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Statements');
    XLSX.writeFile(wb, 'selected_statements.xlsx');
  }

  /**
   * Export selected statements to Word (.doc via HTML)
   */
  exportSelectedToWord(): void {
    const selected = this.statements.filter(s => this.selectedStatementIds.includes(s.id));
    if (selected.length === 0) return;

    const rows = selected.map((s, i) => {
      const refs = Array.isArray(s.reference_links)
        ? s.reference_links.map(r => (typeof r === 'string' ? r : (r as any)?.url || (r as any)?.title || '')).join('<br>')
        : '';
      const complianceRisk = s.compliance?.off_label_risk || (s.compliance as any)?.off_label_tone_risk_flag || '';
      return `
        <tr>
          <td>${i + 1}</td>
          <td>${this.escapeHtml(s.statement_text || '')}</td>
          <td>${this.escapeHtml(s.section || '')}</td>
          <td>${this.escapeHtml(s.rationale || '')}</td>
          <td>${this.escapeHtml(s.why_it_matters || '')}</td>
          <td>${this.escapeHtml(s.level_of_evidence || '')}</td>
          <td>${this.escapeHtml(s.grade_of_recommendation || '')}</td>
          <td>${this.escapeHtml(String(complianceRisk))}</td>
          <td>${s.confidence_score != null ? Math.round(s.confidence_score * 100) + '%' : ''}</td>
          <td>${this.escapeHtml(s.comment || '')}</td>
          <td>${refs}</td>
        </tr>`;
    }).join('');

    const html = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office'
            xmlns:w='urn:schemas-microsoft-com:office:word'
            xmlns='http://www.w3.org/TR/REC-html40'>
      <head><meta charset='utf-8'><title>Selected Statements</title>
      <style>
        body { font-family: Calibri, sans-serif; font-size: 10pt; }
        h1 { font-size: 16pt; color: #6945AF; }
        table { border-collapse: collapse; width: 100%; }
        th { background: #6945AF; color: white; padding: 6px 8px; text-align: left; font-size: 9pt; }
        td { border: 1px solid #ccc; padding: 5px 8px; font-size: 9pt; vertical-align: top; }
        tr:nth-child(even) td { background: #f5f0ff; }
      </style>
      </head>
      <body>
        <h1>Selected Statements (${selected.length})</h1>
        <table>
          <thead><tr>
            <th>#</th>
            <th>Statement</th>
            <th>Section</th>
            <th>Rationale</th>
            <th>Why It Matters</th>
            <th>Level of Evidence</th>
            <th>Grade of Recommendation</th>
            <th>Compliance Risk</th>
            <th>Confidence Score</th>
            <th>Comment</th>
            <th>References</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </body></html>`;

    const blob = new Blob([html], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'selected_statements.doc';
    a.click();
    URL.revokeObjectURL(url);
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Get evidence badge class
   */
  getEvidenceBadgeClass(level: string): string {
    if (!level) return 'evidence-default';
    const lowerLevel = level.toLowerCase();
    if (lowerLevel.includes('1a')) return 'evidence-1a';
    if (lowerLevel.includes('1b')) return 'evidence-1b';
    if (lowerLevel.includes('2a')) return 'evidence-2a';
    if (lowerLevel.includes('2b')) return 'evidence-2b';
    if (lowerLevel.includes('3')) return 'evidence-3';
    return 'evidence-default';
  }

  /**
   * Get grade badge class
   */
  getGradeBadgeClass(grade: string): string {
    if (!grade) return 'grade-default';
    return `grade-${grade.toUpperCase().toLowerCase()}`;
  }

  /**
   * Get compliance risk badge class
   */
  getComplianceRiskClass(risk: string): string {
    if (!risk) return 'risk-default';
    return `risk-${risk}`;
  }

  isReferenceLink(ref: any): boolean {
    if (typeof ref === 'string') {
      return ref.startsWith('http://') || ref.startsWith('https://');
    }
    const refId: string = ref?.ref_id || '';
    return !!(
      ref?.url || ref?.link || ref?.href ||
      refId.startsWith('pmid:') || refId.startsWith('doi:') ||
      ref?.pmid || ref?.doi
    );
  }

  getReferenceUrl(ref: any): string {
    if (typeof ref === 'string') return ref;
    if (ref?.url) return ref.url;
    if (ref?.link) return ref.link;
    if (ref?.href) return ref.href;
    const refId: string = ref?.ref_id || '';
    if (refId.startsWith('pmid:')) {
      return `https://pubmed.ncbi.nlm.nih.gov/${refId.replace('pmid:', '')}`;
    }
    if (refId.startsWith('doi:')) {
      return `https://doi.org/${refId.replace('doi:', '')}`;
    }
    if (ref?.pmid) return `https://pubmed.ncbi.nlm.nih.gov/${ref.pmid}`;
    if (ref?.doi) return `https://doi.org/${ref.doi}`;
    return '#';
  }

  getReferenceLabel(ref: any): string {
    if (typeof ref === 'string') return ref;
    return ref?.title || ref?.label || ref?.text || ref?.ref_id || ref?.url || ref?.link || '';
  }

  getReferenceDetail(ref: any): string {
    if (typeof ref === 'string') return ref;
    const parts: string[] = [];
    if (ref?.title) parts.push(ref.title);
    if (ref?.authors) parts.push(ref.authors);
    if (ref?.year) parts.push(String(ref.year));
    if (ref?.source) parts.push(ref.source);
    if (ref?.ref_id && !ref?.title) parts.push(ref.ref_id);
    return parts.join(' — ') || JSON.stringify(ref);
  }

  /**
   * Get confidence score color
   */
  getConfidenceColor(score: number): string {
    if (score >= 0.8) return 'high';
    if (score >= 0.6) return 'medium';
    return 'low';
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.selectedSection = 'all';
    this.selectedEvidenceLevel = 'all';
    this.selectedComplianceRisk = 'all';
    this.searchText = '';
    this.showOnlySelected = false;
  }
}
